<template>
	<MainContainer hideHeader hideFooter :fetched="!!slides" :loading="pending" :error="error" @try-again="refresh">
		<AuthHeader show-icon />
		<div class="flex flex-col flex-grow px-5 pb-5">
			<Swiper
				class="w-full mb-2 auth-slider-swiper"
				:slides-per-view="1"
				:pagination="true"
				loop
				:autoplay="{
					pauseOnMouseEnter: true,
					delay: 2000
				}"
				:space-between="20"
				:modules="[Autoplay, Pagination]"
				@after-init="(s) => (swiper = s)"
			>
				<SwiperSlide v-for="(slide, i) in slides" :key="i" class="relative flex flex-col">
					<img
						:src="runtimeConfig.public.baseUrl + slide.image_path"
						alt=""
						class="object-cover w-full h-full mb-8 rounded-lg aspect-video"
					/>
				</SwiperSlide>
			</Swiper>
			<div>
				<span class="mb-12 text-3xl font-black line-clamp-2">
					{{ slideTitles[swiper?.realIndex || 0] || currentSlide?.title || "~" }}
				</span>
			</div>
			<div class="flex-grow"></div>
			<div class="flex flex-col mb-10 space-y-3">
				<NuxtLink
					:to="{
						path: $routeUrl.LoginUrl(),
						query: {
							login: '1'
						}
					}"
					class="relative flex items-center px-4 py-3 border ripple-bg-white border-slate-700 rounded-xl"
				>
					<Icon name="mdi:login" class="absolute w-5 h-5 me-4 start-0 ms-4" />
					<span class="flex-grow text-sm font-bold text-center">
						{{ $t("login") }}
					</span>
				</NuxtLink>
				<NuxtLink
					:to="{
						path: $routeUrl.LoginUrl(),
						query: {
							register: '1'
						}
					}"
					class="relative flex items-center px-4 py-3 border ripple-bg-white border-slate-700 rounded-xl"
				>
					<Icon name="mdi:account-add" class="absolute w-5 h-5 start-0 ms-4" />
					<span class="flex-grow text-sm font-bold text-center">
						{{ $t("register") }}
					</span>
				</NuxtLink>
			</div>
			<div class="flex items-center -mb-2">
				<div
					class="flex items-center self-start p-2 cursor-pointer -ms-2"
					@click="state.isSelectLangDialogVisible = true"
				>
					<Icon name="material-symbols:language" class="w-5 h-5 me-1" />
					<span class="text-sm font-bold uppercase">
						{{ $i18n.locale }}
					</span>
				</div>
				<div class="flex-grow" />
				<div class="flex items-center -mx-3 border-collapse border-slate-600">
					<NuxtLink
						v-for="(item, i) in footerItems"
						:key="i"
						:to="item.link"
						class="px-3 text-xs duration-300 text-slate-600 hover:text-primary border-e last:border-none border-slate-300"
					>
						{{ item.title }}
					</NuxtLink>
				</div>
			</div>
			<MainLanguageSelectDialog
				:visible="state.isSelectLangDialogVisible"
				@close="state.isSelectLangDialogVisible = false"
			/>
		</div>
	</MainContainer>
</template>

<script setup lang="ts">
import type { Swiper } from "swiper"
import { Pagination, Autoplay } from "swiper/modules"
const slideStore = useSlideStore()
const runtimeConfig = useRuntimeConfig()
const app = useNuxtApp()

definePageMeta({
	middleware: ["noauth"]
})

const state = reactive({
	isSelectLangDialogVisible: false
})

const swiper = ref<Swiper>()

const currentSlide = computed(() => slides.value?.find((_, i) => i === swiper.value?.realIndex))

const slideTitles = [
	app.$i18n.t("find_free_stuff_nearby"),
	app.$i18n.t("connect_with_your_community"),
	app.$i18n.t("discover_local_businesses")
]

const footerItems = [
	{
		id: "terms",
		title: app.$i18n.t("terms_of_service"),
		link: app.$routeUrl.TermsOfServiceUrl()
	},
	{
		id: "privacy",
		title: app.$i18n.t("privacy_policy"),
		link: app.$routeUrl.PrivacyPolicyUrl()
	}
]

const { data: slides, pending, error, refresh } = await useAsyncData(() => slideStore.getAllSlides())
</script>

<style>
.auth-slider-swiper .swiper-pagination-bullet {
	@apply duration-300 rounded-full shadow-md;
}
.auth-slider-swiper .swiper-pagination-bullet-active {
	@apply bg-primary w-6;
}
</style>
